import { z } from 'zod';
import { hexColorSchema } from './devices.ts';
import { historyInstantSchema, type IndicatorRefDto } from './readings.ts';

/**
 * Saved charts ("Grafiklar boshqaruvi", docs/design/03-modules.md §6): a named selection of indicators with colours,
 * an optional saved period ("Davrni ham saqlash") and the view mode. Personal by default; `isShared` makes a chart
 * visible to everyone with `charts.view` in the tenant. Data comes from `GET /tenants/:t/readings`.
 */

/** Series palette of the design: 4 [D] + 4 [P] colours, cycled. */
export const CHART_SERIES_PALETTE = [
  '#0275FF',
  '#1ECD15',
  '#F4B400',
  '#7059FF',
  '#F48200',
  '#DF0259',
  '#35B5EC',
  '#509F67',
] as const;

export const CHART_PERIOD_PRESETS = ['1d', '7d', '30d', 'all', 'custom'] as const;
export type ChartPeriodPreset = (typeof CHART_PERIOD_PRESETS)[number];
/** "30 KUN" is preselected when a chart has no saved period. */
export const DEFAULT_CHART_PERIOD: ChartPeriodPreset = '30d';

export const chartPeriodSchema = z
  .object({
    preset: z.enum(CHART_PERIOD_PRESETS),
    from: historyInstantSchema.optional(),
    to: historyInstantSchema.optional(),
  })
  .refine((p) => p.preset !== 'custom' || (p.from && p.to), {
    message: 'Maxsus davr uchun boshlanish va tugash sanasi kerak',
    path: ['from'],
  });
export type ChartPeriod = z.infer<typeof chartPeriodSchema>;

export const CHART_VIEW_MODES = ['chart', 'table'] as const;
export type ChartViewMode = (typeof CHART_VIEW_MODES)[number];

export const chartSeriesInputSchema = z.object({
  indicatorId: z.uuid(),
  /** null = indicator colour or the palette */
  color: hexColorSchema.nullish(),
});

const seriesList = z
  .array(chartSeriesInputSchema)
  .min(1)
  .max(50)
  .refine((s) => new Set(s.map((x) => x.indicatorId)).size === s.length, {
    message: "Ko'rsatkichlar takrorlanmasin",
  });

export const createChartSchema = z.object({
  /** "GRAFIK NOMI" — unique per user in the tenant */
  name: z.string().trim().min(1).max(80),
  isShared: z.boolean().default(false),
  /** null = "Davrni ham saqlash" off */
  period: chartPeriodSchema.nullable().default(null),
  viewMode: z.enum(CHART_VIEW_MODES).default('chart'),
  series: seriesList,
});
export type CreateChartInput = z.infer<typeof createChartSchema>;

export const updateChartSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  isShared: z.boolean().optional(),
  period: chartPeriodSchema.nullable().optional(),
  viewMode: z.enum(CHART_VIEW_MODES).optional(),
  /** replaces the whole selection */
  series: seriesList.optional(),
  /** tab position */
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type UpdateChartInput = z.infer<typeof updateChartSchema>;

export interface ChartSeriesDto {
  indicatorId: string;
  /** resolved colour: custom → indicator colour → palette */
  color: string;
  /** the colour picked for this chart (null = automatic) */
  customColor: string | null;
  sortOrder: number;
  /** null when the indicator was deleted */
  indicator: IndicatorRefDto | null;
}

export interface ChartDto {
  id: string;
  tenantId: string;
  name: string;
  isShared: boolean;
  owner: { id: string; fullName: string } | null;
  isOwner: boolean;
  period: ChartPeriod | null;
  viewMode: ChartViewMode;
  sortOrder: number;
  series: ChartSeriesDto[];
  canEdit: boolean;
  canDelete: boolean;
  createdAt: string;
  updatedAt: string;
}
